<script setup>
import { computed } from 'vue';

import { ArrowRight } from 'lucide-vue-next';

const props = defineProps({
  // Pre-built change rows (see buildChangeRows in helpers/entityChangeFormat)
  rows: {
    type: Array,
    required: true,
  },
  // The notification.data payload — used only for the truncation hint
  data: {
    type: Object,
    default: () => ({}),
  },
  // Max change rows to render inline before collapsing into "+N more"
  maxRows: {
    type: Number,
    default: 4,
  },
});

const visibleRows = computed(() => props.rows.slice(0, props.maxRows));

const moreText = computed(() => {
  const hidden = props.rows.length - visibleRows.value.length;
  if (hidden > 0) return `+${hidden} more`;

  const entities = Array.isArray(props.data?.entities) ? props.data.entities : [];
  const truncated = props.data?.truncated || entities.some((e) => e.fields_truncated);
  return truncated ? '+ more changes' : '';
});
</script>

<template>
  <div class="nec">
    <div
      v-for="row in visibleRows"
      :key="row.key"
      v-no-translate
      class="nec-row"
    >
      <span v-if="row.showEntity" class="nec-entity">
        {{ row.entityLabel }}<template v-if="row.entityId"> #{{ row.entityId }}</template>
      </span>
      <span class="nec-field">{{ row.label }}</span>
      <span class="nec-values">
        <span
          v-if="row.oldText !== null && row.oldText !== ''"
          class="nec-old"
          :class="{ 'nec-old--removed': row.changeType === 'DELETED' }"
        >{{ row.oldText }}</span>
        <ArrowRight
          v-if="row.oldText !== null && row.oldText !== '' && row.newText !== null && row.newText !== ''"
          :size="11"
          class="nec-arrow"
        />
        <span v-if="row.newText !== null && row.newText !== ''" class="nec-new">{{ row.newText }}</span>
      </span>
    </div>

    <div v-if="moreText" class="nec-more">
      {{ moreText }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.nec {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 2px;
}

.nec-row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 6px;
  font-size: 12px;
  line-height: 1.4;
  min-width: 0;
}

.nec-entity {
  color: var(--wp-text-tertiary);
  font-weight: 600;
  white-space: nowrap;
}

.nec-field {
  color: var(--wp-text-tertiary);
  font-weight: 500;
  white-space: nowrap;

  &::after {
    content: ':';
  }
}

.nec-values {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
}

.nec-old {
  color: var(--wp-text-tertiary);
  word-break: break-word;

  &--removed {
    text-decoration: line-through;
  }
}

.nec-arrow {
  flex-shrink: 0;
  align-self: center;
  color: var(--wp-text-tertiary);
}

.nec-new {
  color: var(--wp-text-primary);
  font-weight: 600;
  word-break: break-word;
}

.nec-more {
  font-size: 11px;
  color: var(--wp-text-accent);
}
</style>
